/**
 * Friendly casts — heals and helpful buffs (stream note 14, 2026-10-08).
 *
 * One rule, used by the client's cast prediction and the server's authority:
 * a friendly cast lands on the targeted player when that player is a friend,
 * and on the caster otherwise. An enemy target (a mob, or a hostile player on
 * a PvP map) is not a reason to refuse or redirect the cast — it is ignored,
 * so a priest fighting the Stone Golem can still heal herself.
 */

/** Authored target kinds whose cast is a friendly cast. */
export function isFriendlyCastTarget(authoredTarget: string | undefined): boolean {
  return authoredTarget === 'friendly';
}

export interface FriendlyCastAbility {
  target?: string;
  abilityType?: string;
}

/**
 * A heal centred on the caster (`ability_type = "heal"`, `target = "aoe"`:
 * Chain Heal, Prayer of Mending). It has no target at all: the selected actor
 * is neither a recipient nor a range anchor (plan 29 bugs 3 and 10 — with an
 * enemy selected, Chain Heal's range 40 was measured to the enemy and the
 * cast refused, and Prayer of Mending drew its splash on the enemy).
 */
export function isAreaHeal(ability: FriendlyCastAbility | null | undefined): boolean {
  return ability?.abilityType === 'heal' && ability.target === 'aoe';
}

/**
 * A friendly cast: an authored `target = "friendly"`, or any heal. It keeps
 * only a friendly player as its target (an enemy selection means "me").
 */
export function isFriendlyCast(ability: FriendlyCastAbility | null | undefined): boolean {
  return isFriendlyCastTarget(ability?.target) || ability?.abilityType === 'heal';
}

export interface FriendlyRecipientInput {
  casterId: string;
  selectedTargetId?: string;
  /** True when the id names a player the caster may help (never the caster). */
  isFriendlyPlayer: (id: string) => boolean;
}

/**
 * The id a friendly cast carries on the wire: the friendly player's id, or
 * `undefined` for the caster themself.
 */
export function resolveFriendlyTargetId(input: FriendlyRecipientInput): string | undefined {
  const { casterId, selectedTargetId, isFriendlyPlayer } = input;
  if (!selectedTargetId || selectedTargetId === casterId) return undefined;
  return isFriendlyPlayer(selectedTargetId) ? selectedTargetId : undefined;
}

export interface HealAmountAbility {
  baseDamage?: number;
}

export interface HealAmountCaster {
  maxHealth?: number;
  /** The server Player keeps primary stats top-level; the client in `stats`. */
  intelligence?: number;
  stats?: { maxHp?: number; intelligence?: number };
}

/** Default base heal when the TOML authors no `base_damage`. */
export const DEFAULT_HEAL_BASE = 20;
/** Share of the caster's max HP added to every heal. */
export const HEAL_MAX_HP_SHARE = 0.15;

/**
 * The amount a heal ability restores — the formula the client's generic heal
 * has always predicted, now the server's too, so the two agree.
 */
export function abilityHealAmount(ability: HealAmountAbility | null | undefined, caster: HealAmountCaster): number {
  const maxHp = caster.stats?.maxHp ?? caster.maxHealth ?? 100;
  const int = caster.stats?.intelligence ?? caster.intelligence ?? 0;
  const base = ability?.baseDamage ?? DEFAULT_HEAL_BASE;
  return Math.max(0, Math.floor(base + maxHp * HEAL_MAX_HP_SHARE + int));
}

export interface HealShapeAbility {
  abilityType?: string;
  target?: string;
  durationMs?: number;
  tags?: string[];
  mechanicType?: string;
}

export type HealShape = 'single' | 'hot' | 'area' | 'none';

/**
 * How the server delivers a heal ability. Ground-placed heals (totems,
 * consecrated ground) keep their aura path, which already heals through the
 * server's `healPlayer` route.
 */
export function healShape(ability: HealShapeAbility | null | undefined): HealShape {
  if (!ability || ability.abilityType !== 'heal') return 'none';
  if (ability.target === 'ground') return 'none';
  if (ability.target === 'aoe') return 'area';
  const overTime = (ability.durationMs ?? 0) > 0
    && ((ability.tags ?? []).includes('hot') || ability.mechanicType === 'channel');
  return overTime ? 'hot' : 'single';
}
